import { useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { projectService } from '../../api/client';
import type { Project } from '../../gen/ejot/roofing/v1/types';

export function ProjectDialog({
  open,
  onClose,
  onOpen,
}: {
  open: boolean;
  onClose: () => void;
  onOpen: (project: Project) => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: ({ signal }) => projectService.listProjects({ page_size: 100, include_archived: false }, signal),
    enabled: open,
  });
  const load = useMutation({
    mutationFn: (projectId: string) => projectService.getProject({ project_id: projectId }),
    onSuccess: (response) => {
      if (!response.project) return;
      onOpen(response.project);
      onClose();
    },
  });

  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="open-project-title"
        tabIndex={-1}
        ref={dialogRef}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => { if (event.key === 'Escape') onClose(); }}
      >
        <div className="dialog-header">
          <h2 className="dialog-title" id="open-project-title">Open project</h2>
          <button type="button" className="header-button" onClick={onClose}>Close</button>
        </div>
        <div className="dialog-body">
          {projects.isPending && <p className="field-note" role="status">Loading projects…</p>}
          {projects.isError && (
            <p className="field-note field-note-blocking" role="alert">
              Projects could not be loaded: {String(projects.error)}
            </p>
          )}
          {!projects.isPending && !projects.isError && (projects.data?.projects ?? []).length === 0 && (
            <p className="field-note">No saved projects are available.</p>
          )}
          {(projects.data?.projects ?? []).length > 0 && (
            <table className="engineering-table">
              <thead>
                <tr>
                  <th scope="col">Project</th>
                  <th scope="col">Subject</th>
                  <th scope="col">Updated</th>
                  <th scope="col">Last status</th>
                  <th scope="col"><span className="sr-only">Action</span></th>
                </tr>
              </thead>
              <tbody>
                {projects.data?.projects?.map((summary) => (
                  <tr key={summary.id}>
                    <td>{summary.project_name || 'Untitled project'}</td>
                    <td>{summary.subject || '-'}</td>
                    <td>{summary.updated_at || '-'}</td>
                    <td>{summary.last_calculation_status || '-'}</td>
                    <td>
                      <button
                        type="button"
                        className="toolbar-button"
                        disabled={!summary.id || load.isPending}
                        onClick={() => summary.id && load.mutate(summary.id)}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {load.isError && (
            <p className="field-note field-note-blocking" role="alert">
              The selected project could not be opened: {String(load.error)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
